// SLIME DUEL — sub & special previews on the locker / shop pedestal (showcase.js). Picking a sub or a special in the
// Shop or the Loadout plays a short scene of the slime dualist USING it: the sub is held, thrown and goes off (a bomb
// bursts, a sprinkler sprays, a curtain pours, a seeker scuttles round the deck…); the special runs its signature move
// (the Crab Rig walks, fires its gatling and mortar and rolls into a ball, the Kraken hops, the Jetpack hovers…).
//
// Everything is visual only and lives in the pedestal's stage frame (`root` = showcase fxRoot: turns with the turntable,
// y = 0 at the pedestal top, the kid stands at the origin facing KID_YAW). It reuses the real props (getSubDef,
// getSpecialProp, the sp_* held weapons) and the pedestal's own ink FX (InkFX drops / splats / ripples, Sparkles).
//
//   const kit = new PedestalKit(root)
//   kit.play('sub' | 'special', id, character, color, weapon)   weapon = the kid's own (put back afterwards)
//   pose = kit.update(dt, ctx)   // ctx { c, fx, sparks, deckY }; null when idle, else overrides for the kid this frame:
//                                //   { y, sy, scale, twirl, firing, subAim, hide, grounded }
//   kit.stop(c, weapon)          // cut it short (another item picked, screen left) and give the kid back their kit
import * as THREE from 'three';
import { G } from '../core/ctx.js';
import { SUBS, SPECIALS } from '../config.js';
import { getSubDef } from './character-weapons.js';
import { getSpecialProp } from './special-props.js';
import { getPlasticMaterial, getInkMaterial } from './character-mats.js';

const TAU = Math.PI * 2;
const KID_YAW = -0.45;                      // the pedestal kid's facing in the stage frame (showcase _updateLoadout)
const FWD = new THREE.Vector3(Math.sin(KID_YAW), 0, Math.cos(KID_YAW));
const SIDE = new THREE.Vector3(Math.cos(KID_YAW), 0, -Math.sin(KID_YAW));   // the kid's left
const SUB_SCALE = 1.9;                      // subs.js: hand-scale props read at bomb size in the world
const clamp = (x, a, b) => (x < a ? a : x > b ? b : x);
const c01 = (x) => clamp(x, 0, 1);
const eOut = (x) => 1 - (1 - c01(x)) ** 3;
const backOut = (x, s = 1.7) => { x = c01(x) - 1; return x * x * ((s + 1) * x + s) + 1; };
let muted = false;   // tile portraits (showcase.js portrait kind 'special') run a preview off-screen: no sound
export const setKitMuted = (m) => { muted = !!m; };
const play = (name, o) => (muted ? null : G.audio?.play?.(name, o));
const _v = new THREE.Vector3(), _v2 = new THREE.Vector3();

// lightweight meshes from prop geometry sets: plastic body + team ink (+ glow)
function propGroup(d, color, glowMat) {
  const g = new THREE.Group();
  if (d.body) { const m = new THREE.Mesh(d.body, getPlasticMaterial()); m.castShadow = true; g.add(m); }
  if (d.ink) { const m = new THREE.Mesh(d.ink, getInkMaterial(color)); m.castShadow = true; g.add(m); }
  if (d.glow && glowMat) g.add(new THREE.Mesh(d.glow, glowMat));
  return g;
}
function partGroup(body, ink, color) {
  const g = new THREE.Group();
  if (body) { const m = new THREE.Mesh(body, getPlasticMaterial()); m.castShadow = true; g.add(m); }
  if (ink) { const m = new THREE.Mesh(ink, getInkMaterial(color)); m.castShadow = true; g.add(m); }
  return g;
}
/** A pivoted part: group at `at`, mesh inside at −at (rotate the group to turn the part about its pivot). */
function pivotGroup(body, ink, at, color) {
  const g = new THREE.Group(); g.position.copy(at);
  const m = partGroup(body, ink, color); m.position.copy(at).negate(); g.add(m);
  return g;
}

export class PedestalKit {
  constructor(root) {
    this.root = new THREE.Group();
    root.add(this.root);
    this.run = null;
    this.sphere = new THREE.IcosahedronGeometry(1, 3);
    this.ring = new THREE.TorusGeometry(1, 0.03, 8, 64);
  }
  get active() { return !!this.run; }

  play(type, id, c, color, weapon) {
    if (this.run) this.stop(c, weapon);
    const P = type === 'sub' ? subPreview(id) : specialPreview(id);
    if (!P || !c) return false;
    this.color = color.clone();
    this.run = { P, t: 0, id, type, c, s: {}, objs: [], mats: [], ended: false, home: weapon || c.weaponId || c.weaponKind };
    this.glow = this._mat(new THREE.MeshStandardMaterial({ color: this.color.clone().multiplyScalar(0.35), emissive: this.color.clone(), emissiveIntensity: 1.2, roughness: 0.35 }));
    P.start?.call(this, this.run.s, c);
    return true;
  }

  stop(c, weapon) {
    const R = this.run;
    if (!R) return;
    this.run = null;
    try { R.P.end?.call(this, R.s, R.c || c); } catch (e) { console.warn('[kit] end', e); }
    for (const o of R.objs) o.removeFromParent();
    for (const m of R.mats) m.dispose();
    const k = R.c || c;
    if (k) {
      k.weaponHidden = false; k.subPropHidden = false;
      if (k.weapon?.pivot) k.weapon.pivot.visible = true;
      if (k.weapon?.left?.pivot) k.weapon.left.pivot.visible = true;
      if (!k.visible) k.setVisible(true);
      const home = weapon || R.home;
      if (home && R.s.swapped) k.setWeapon(home);
    }
  }

  update(dt, ctx) {
    const R = this.run;
    if (!R) return null;
    R.t += dt;
    this.ctx = ctx;
    const pose = { y: 0, sy: 1, scale: 1, twirl: 0, firing: false, subAim: false, hide: false, grounded: true };
    let done = false;
    try { done = R.P.tick.call(this, R.s, R.t, dt, pose, R.c) === true; } catch (e) { console.warn('[kit] tick', e); done = true; }
    if (done || R.t > (R.P.dur || 4)) { this.stop(R.c, ctx.weapon); return pose; }
    return pose;
  }

  // ---------------------------------------------------------------- helpers (stage frame)
  _add(o) { this.root.add(o); this.run.objs.push(o); return o; }
  _drop(o) { o.removeFromParent(); }
  _mat(m) { if (this.run) this.run.mats.push(m); return m; }
  _ghost(opacity = 0.35, tint = 1) {
    return this._mat(new THREE.MeshBasicMaterial({ color: this.color.clone().multiplyScalar(tint), transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide }));
  }
  /** Where the kid's left hand is (stage frame) — subs leave from it. */
  _hand(c, out) {
    const hb = c.bones?.handL;
    if (hb) { hb.getWorldPosition(out); this.root.updateWorldMatrix(true, false); this.root.worldToLocal(out); }
    else out.set(0, 1.1, 0).addScaledVector(SIDE, 0.2);
    return out;
  }
  _head(c, out) {
    if (c.getHeadPosition) { c.getHeadPosition(out); this.root.updateWorldMatrix(true, false); this.root.worldToLocal(out); }
    else out.set(0, 1.45, 0);
    return out;
  }
  /** A point on the deck `d` ahead of the kid, `s` to their left. */
  _ahead(d, s = 0, out = new THREE.Vector3()) { return out.copy(FWD).multiplyScalar(d).addScaledVector(SIDE, s).setY(this.ctx?.deckY ?? 0.014); }
  _subProp(kind) {
    const d = getSubDef(kind);
    if (d._top == null) { d.body.computeBoundingBox(); d._top = d.body.boundingBox.max.y; }
    const outer = new THREE.Group(), model = new THREE.Group();
    model.scale.setScalar(SUB_SCALE);
    model.add(propGroup(d, this.color, this.glow));
    let spin = null;
    if (d.spin) { spin = pivotGroup(d.spin, d.spinInk, d.spinAt, this.color); model.add(spin); }
    outer.add(model);
    outer.userData = { spin, model, top: d._top * SUB_SCALE };
    return this._add(outer);
  }
  // ink bursts
  _blast(p, power = 1, n = 24) {
    const fx = this.ctx.fx, sp = this.ctx.sparks;
    fx.crown(p.x, this.ctx.deckY, p.z, 0.8 * power, n, 0.12 * power);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU + Math.random();
      fx.drop(p.x, p.y + 0.05, p.z, Math.sin(a) * 1.4 * power, 2 + Math.random() * 1.6 * power, Math.cos(a) * 1.4 * power, 0.03 + 0.02 * power, 2, 3);
    }
    fx.ripple(p.x, this.ctx.deckY, p.z, 0.05, 0.5 + 0.45 * power, 0.6);
    for (let i = 0; i < 10; i++) sp.spawn(p.x + (Math.random() - 0.5) * 0.4 * power, p.y + Math.random() * 0.5 * power, p.z + (Math.random() - 0.5) * 0.4 * power, 0.08 + Math.random() * 0.08, i % 2 ? this.color : new THREE.Color(1, 0.97, 0.85), 0.5 + Math.random() * 0.4);
    G.audio && play(power > 1.2 ? 'bomb_explode' : 'splat_big', { volume: 0.5 });
  }
  _spray(p, dir, n, speed, r = 0.014, kind = 1) {
    for (let i = 0; i < n; i++) {
      const j = 0.35;
      this.ctx.fx.drop(p.x, p.y, p.z, (dir.x + (Math.random() - 0.5) * j) * speed, (dir.y + (Math.random() - 0.5) * j) * speed, (dir.z + (Math.random() - 0.5) * j) * speed, r * (0.7 + Math.random() * 0.6), kind, 3);
    }
  }
}

// ================================================================================================ subs
// Shared flow: hold the sub cocked (0 → HOLD), throw it on an arc to a spot on the deck, then the kind's own behaviour.
const HOLD = 0.5, FLY = 0.5;
function thrown(kind, opts = {}) {
  const land = opts.land || [0.42, 0.06];
  return {
    dur: opts.dur || 3,
    start(s, c) { c.setSub?.(kind); s.prop = null; s.phase = 'hold'; },
    tick(s, t, dt, pose, c) {
      if (s.phase === 'hold') {
        pose.subAim = true;
        if (t >= HOLD) {
          s.phase = 'fly'; s.t0 = t;
          c.trigger('throw');
          play('bomb_throw', { volume: 0.5 });
          s.prop = this._subProp(kind);
          s.from = this._hand(c, new THREE.Vector3());
          s.to = this._ahead(land[0], land[1]);
          s.prop.position.copy(s.from);
        }
        return;
      }
      const P = s.prop, tl = t - s.t0;
      if (s.phase === 'fly') {
        const x = c01(tl / (opts.fly || FLY)), H = opts.arc ?? 0.55;
        P.position.lerpVectors(s.from, s.to, x);
        P.position.y = s.from.y + (s.to.y - s.from.y) * x + 4 * H * x * (1 - x);
        P.rotation.x += dt * 9; P.rotation.z += dt * 4;
        if (x >= 1) {
          s.phase = 'on'; s.t1 = t; P.rotation.set(0, KID_YAW, 0);
          this.ctx.fx.splat(s.to.x, this.ctx.deckY, s.to.z, 0.09, 2.5);
          this.ctx.fx.ripple(s.to.x, this.ctx.deckY, s.to.z, 0.02, 0.25, 0.4);
          play('bomb_beep', { volume: 0.4 });
          opts.landed?.call(this, s, P);
        }
        return;
      }
      if (s.phase === 'on') {
        const r = opts.on.call(this, s, t - s.t1, dt, P, c);
        if (r === 'boom') { this._blast(P.position, opts.power || 1); this._drop(P); s.phase = 'after'; s.t2 = t; }
        else if (r === 'gone') { this._drop(P); s.phase = 'after'; s.t2 = t; }
        return;
      }
      if (s.phase === 'after' && t - s.t2 > 0.6) return true;
    },
  };
}
const blink = (P, tl, rate = 9) => { const k = 1 + 0.12 * Math.max(0, Math.sin(tl * rate * (1 + tl))); P.scale.setScalar(k); };

function subPreview(id) {
  const kind = (SUBS[id] && SUBS[id].kind) || id;
  switch (kind) {
    case 'bomb': return thrown(kind, { on(s, tl, dt, P) { // a hop on landing, the fuse beeps faster, then it bursts
      P.position.y = s.to.y + Math.max(0, 0.12 * Math.sin(Math.PI * c01(tl / 0.25)));
      blink(P, tl); return tl > 0.95 ? 'boom' : null; }, power: 1.2 });
    case 'sticky': return thrown(kind, { on(s, tl, dt, P) { blink(P, tl, 6); return tl > 1.3 ? 'boom' : null; }, power: 1.5, dur: 3.4 });
    case 'burst': return thrown(kind, { arc: 0.3, fly: 0.35, on() { return 'boom'; }, power: 0.9 });
    case 'seeker':
    case 'waddle': return thrown(kind, { land: [0.45, 0.1], dur: 3.6, on(s, tl, dt, P) {
      // scuttles round the deck leaving a slime trail, then bursts
      const a0 = Math.atan2(s.to.x, s.to.z), a = a0 + tl * (kind === 'seeker' ? 2.6 : 1.6), r = 0.45;
      P.position.set(Math.sin(a) * r, s.to.y, Math.cos(a) * r);
      P.rotation.y = a + Math.PI / 2;
      if (kind === 'waddle') { P.rotation.z = Math.sin(tl * 14) * 0.25; P.position.y += Math.abs(Math.sin(tl * 14)) * 0.03; }
      s.trail = (s.trail || 0) + dt;
      if (s.trail > 0.06) { s.trail = 0; this.ctx.fx.splat(P.position.x, this.ctx.deckY, P.position.z, 0.07, 2); }
      return tl > 1.5 ? 'boom' : null; }, power: 1.1 });
    case 'torpedo': return thrown(kind, { land: [0.5, 0], arc: 0.9, dur: 3.2, landed(s, P) { s.hover = P.position.clone(); },
      on(s, tl, dt, P) {
        // pops up, hangs locked on, then darts down and bursts into droplets
        if (tl < 0.5) { P.position.y = s.to.y + 0.7 * eOut(tl / 0.5); P.rotation.y += dt * 5; }
        else { const x = c01((tl - 0.5) / 0.18); P.position.y = s.to.y + 0.7 * (1 - x); }
        if (tl > 0.68) { this._spray(P.position.clone().setY(P.position.y + 0.1), new THREE.Vector3(0, 1, 0), 22, 2.6, 0.016); return 'boom'; }
        return null; }, power: 0.8 });
    case 'tracer': return thrown(kind, { land: [0.3, 0], arc: 0.08, fly: 0.2, dur: 2.4, on(s, tl, dt, P) {
      // skims off the deck and ricochets away, puddling on each bounce
      const p = _v.copy(FWD).multiplyScalar(0.3 + tl * 2.4);
      P.position.set(p.x, s.to.y + Math.abs(Math.sin(tl * 9)) * 0.18, p.z);
      if (Math.sin(tl * 9) * Math.sin((tl - dt) * 9) <= 0) this.ctx.fx.splat(p.x, this.ctx.deckY, p.z, 0.08, 2);
      this.ctx.sparks.spawn(P.position.x, P.position.y + 0.04, P.position.z, 0.05, this.color, 0.4);
      return tl > 0.5 ? 'gone' : null; } });
    case 'boomerang': return thrown(kind, { land: [0.5, 0.2], arc: 0.6, dur: 3.4, landed(s) { s.a0 = Math.atan2(s.to.x, s.to.z); }, on(s, tl, dt, P) {
      // whirls a lap round the kid, shredding, then bursts
      const a = s.a0 + tl * 4.2, r = 0.55;
      P.position.set(Math.sin(a) * r, 0.75 + 0.1 * Math.sin(tl * 6), Math.cos(a) * r);
      P.rotation.y += dt * 24;
      if (Math.random() < 0.5) this.ctx.fx.drop(P.position.x, P.position.y, P.position.z, (Math.random() - 0.5), -0.5, (Math.random() - 0.5), 0.012, 1, 2);
      return tl > 1.5 ? 'boom' : null; } });
    case 'scan':
    case 'mist': return thrown(kind, { dur: 3.2, on(s, tl, dt, P) {
      // bursts into a cloud: sensing (team colour) or poison (sickly green)
      if (!s.cloud) {
        s.cloud = this._add(new THREE.Group()); s.cloud.position.copy(P.position);
        const m = this._mat(new THREE.MeshBasicMaterial({ color: kind === 'mist' ? new THREE.Color(0.55, 0.95, 0.35) : this.color.clone().lerp(new THREE.Color(1, 1, 1), 0.3), transparent: true, opacity: 0.3, depthWrite: false }));
        s.puffs = [];
        for (let i = 0; i < 7; i++) { const b = new THREE.Mesh(this.sphere, m); b.userData.o = new THREE.Vector3((Math.random() - 0.5) * 0.5, Math.random() * 0.35, (Math.random() - 0.5) * 0.5); s.cloud.add(b); s.puffs.push(b); }
        s.cm = m;
        this.ctx.fx.ripple(P.position.x, this.ctx.deckY, P.position.z, 0.05, 0.9, 0.8);
        play(kind === 'mist' ? 'bubble_release' : 'sonar_ping', { volume: 0.4 });
        P.visible = false;
      }
      const g = eOut(tl / 0.4), fade = 1 - c01((tl - 1.1) / 0.6);
      s.puffs.forEach((b, i) => { b.position.copy(b.userData.o).multiplyScalar(g).setY(0.15 + b.userData.o.y * g + Math.sin(tl * 2 + i) * 0.03); b.scale.setScalar((0.18 + 0.1 * (i % 3)) * g); });
      s.cm.opacity = 0.32 * fade;
      return tl > 1.7 ? 'gone' : null; } });
    case 'curtain': return thrown(kind, { dur: 3.4, on(s, tl, dt, P) {
      // a sheet of falling slime pours up out of the device
      if (!s.sheet) {
        s.sheet = this._add(new THREE.Mesh(new THREE.PlaneGeometry(0.9, 1.25).translate(0, 0.625, 0), this._ghost(0.5, 1.1)));
        s.sheet.position.copy(P.position); s.sheet.rotation.y = KID_YAW;
      }
      const g = eOut(tl / 0.35), fade = 1 - c01((tl - 1.4) / 0.4);
      s.sheet.scale.set(1, g, 1); s.sheet.material.opacity = 0.5 * fade;
      if (fade > 0.2 && Math.random() < 0.8) {
        const o = (Math.random() - 0.5) * 0.85;
        this.ctx.fx.drop(P.position.x + SIDE.x * o, P.position.y + 1.2 * g, P.position.z + SIDE.z * o, 0, -1.2, 0, 0.012, 4, 2);
      }
      return tl > 1.8 ? 'gone' : null; } });
    case 'sprinkler': return thrown(kind, { dur: 3.6, on(s, tl, dt, P) {
      // spins and sprays slime around in pulses
      const sp = P.userData.spin;
      if (sp) sp.rotation.y += dt * 18;
      const pulse = Math.sin(tl * 7) > 0;
      if (pulse) for (let k = 0; k < 2; k++) {
        const a = tl * 18 + k * Math.PI;
        this.ctx.fx.drop(P.position.x, P.position.y + P.userData.top, P.position.z, Math.sin(a) * 1.3, 1.4, Math.cos(a) * 1.3, 0.014, 1, 2.5);
      }
      return tl > 1.8 ? 'gone' : null; } });
    case 'mine': return thrown(kind, { land: [0.32, -0.12], arc: 0.12, fly: 0.3, dur: 3.2, on(s, tl, dt, P) {
      // hides in the slime, then a foe's step sets it off
      P.position.y = s.to.y - 0.04 * c01(tl / 0.3);
      blink(P, tl, 4);
      return tl > 1.4 ? 'boom' : null; }, power: 0.9 });
    case 'beacon': return thrown(kind, { land: [0.36, -0.15], arc: 0.15, fly: 0.3, dur: 3.2, on(s, tl, dt, P) {
      // a super-jump point: the lamp pings rings up into the sky
      s.ping = (s.ping || 0) - dt;
      if (s.ping <= 0) { s.ping = 0.45; this.ctx.fx.ripple(P.position.x, this.ctx.deckY, P.position.z, 0.04, 0.4, 0.6); for (let i = 0; i < 4; i++) this.ctx.sparks.spawn(P.position.x, P.position.y + 0.2 + i * 0.25, P.position.z, 0.07, this.color, 0.6); play('bomb_beep', { volume: 0.25, pitch: 1.3 }); }
      return tl > 1.6 ? 'gone' : null; } });
    case 'shaker': return thrown(kind, { land: [0.25, 0.05], dur: 3.6, on(s, tl, dt, P) {
      // up to three blasts, each hopping it forward
      const n = Math.floor(tl / 0.45);
      if (n > (s.n ?? -1) && n < 3) { s.n = n; this._blast(P.position, 0.7, 14); }
      const h = tl % 0.45 / 0.45;
      P.position.copy(FWD).multiplyScalar(0.25 + 0.14 * Math.min(3, tl / 0.45)).setY(s.to.y + 0.18 * Math.sin(Math.PI * h));
      return tl > 1.35 ? 'gone' : null; } });
    default: return thrown(kind, { on(s, tl, dt, P) { blink(P, tl); return tl > 0.9 ? 'boom' : null; } });
  }
}

// ================================================================================================ specials
function specialPreview(id) {
  const def = SPECIALS[id];
  if (!def) return null;
  if (id.startsWith('barrage')) return barrage(def.bomb || 'bomb');
  return SP[id] || SP.generic;
}

// Bomb Barrage (+ its Cling / Cannon / Seeker / Mist variants): hold the bombs up and lob five in quick succession
function barrage(bombId) {
  const kind = (SUBS[bombId] && SUBS[bombId].kind) || 'bomb';
  return {
    dur: 3.6,
    start(s, c) { c.setSub?.(kind); s.next = 0.35; s.bombs = []; s.n = 0; },
    tick(s, t, dt, pose, c) {
      pose.subAim = s.n < 5;
      if (s.n < 5 && t >= s.next) {
        s.next = t + 0.32; s.n++;
        c.trigger('throw');
        play('bomb_throw', { volume: 0.4, pitch: 0.9 + s.n * 0.05 });
        const a = KID_YAW + (s.n - 3) * 0.55;
        const P = this._subProp(kind);
        const from = this._hand(c, new THREE.Vector3()), to = new THREE.Vector3(Math.sin(a) * 0.5, this.ctx.deckY, Math.cos(a) * 0.5);
        P.position.copy(from);
        s.bombs.push({ P, from, to, t0: t });
      }
      for (const b of s.bombs) {
        if (b.done) continue;
        const x = c01((t - b.t0) / 0.45);
        b.P.position.lerpVectors(b.from, b.to, x); b.P.position.y += 4 * 0.5 * x * (1 - x); b.P.rotation.x += dt * 8;
        if (t - b.t0 > 0.85) { this._blast(b.P.position, 0.8, 14); this._drop(b.P); b.done = true; }
      }
      return t > 3.1;
    },
  };
}

const SP = {
  // Tidal Slam: leap, hang, slam down into a huge shockwave
  slam: {
    dur: 2.6,
    tick(s, t, dt, pose, c) {
      if (!s.leap) { s.leap = true; c.trigger('special_leap'); play('special_activate', { volume: 0.5 }); }
      if (t < 0.55) { pose.y = 0.95 * eOut(t / 0.55); pose.grounded = false; pose.twirl = TAU * eOut(t / 0.55); }
      else if (t < 0.75) { pose.y = 0.95; pose.grounded = false; if (!s.slam) { s.slam = true; c.trigger('special_slam'); } }
      else if (t < 0.88) { pose.y = 0.95 * (1 - (t - 0.75) / 0.13); pose.grounded = false; pose.sy = 1.15; }
      else if (!s.hit) {
        s.hit = true; c.trigger('land', 14); play('special_slam', { volume: 0.6 });
        const o = new THREE.Vector3(0, this.ctx.deckY, 0);
        this.ctx.fx.crown(0, this.ctx.deckY, 0, 1.6, 34, 0.35);
        for (let k = 0; k < 3; k++) this.ctx.fx.ripple(0, this.ctx.deckY, 0, 0.3 + k * 0.1, 1.2 + k * 0.6, 0.6 + k * 0.2);
        this._blast(o, 1.4, 10);
      } else pose.sy = 1 - 0.18 * Math.exp(-(t - 0.88) * 6) * Math.sin((t - 0.88) * 18);
      return t > 2.2;
    },
  },
  // Tempest: hurl a rain cloud that soaks the deck
  storm: {
    dur: 3.6,
    tick(s, t, dt, pose, c) {
      if (!s.cloud) {
        c.trigger('throw'); play('bomb_throw', { volume: 0.4 });
        s.cloud = this._add(new THREE.Group());
        const m = this._mat(new THREE.MeshStandardMaterial({ color: this.color.clone().multiplyScalar(0.55).lerp(new THREE.Color(0.3, 0.3, 0.4), 0.4), roughness: 0.9, transparent: true, opacity: 0.92 }));
        for (let i = 0; i < 6; i++) { const b = new THREE.Mesh(this.sphere, m); b.position.set((i - 2.5) * 0.12, Math.sin(i * 2.1) * 0.05, Math.cos(i * 1.7) * 0.08); b.scale.setScalar(0.13 + 0.05 * (i % 3)); s.cloud.add(b); }
        s.from = this._hand(c, new THREE.Vector3());
      }
      const x = eOut(t / 0.6);
      s.cloud.position.lerpVectors(s.from, _v2.copy(FWD).multiplyScalar(0.3).setY(1.95), x);
      s.cloud.scale.setScalar(0.3 + 0.9 * x);
      s.cloud.rotation.y += dt * 0.6;
      if (t > 0.6 && t < 2.8) {
        if (!s.rain) { s.rain = true; play('storm_rain', { volume: 0.4 }); }
        for (let k = 0; k < 3; k++) this.ctx.fx.drop(s.cloud.position.x + (Math.random() - 0.5) * 0.6, s.cloud.position.y - 0.1, s.cloud.position.z + (Math.random() - 0.5) * 0.5, 0, -2.5, 0, 0.01, 4, 2);
      }
      if (t > 2.8) s.cloud.scale.multiplyScalar(Math.exp(-dt * 6));
      return t > 3.3;
    },
  },
  // Bubbler: a wobbling force field around the kid, popped at the end
  bubbler: {
    dur: 3,
    tick(s, t, dt, pose, c) {
      if (!s.b) { s.b = this._add(new THREE.Mesh(this.sphere, this._ghost(0.28, 1.3))); s.b.position.set(0, 0.78, 0); play('shield_up', { volume: 0.5 }); c.trigger('hairflip'); }
      const g = backOut(t / 0.4, 2.2), w = Math.sin(t * 7) * 0.04;
      s.b.scale.set(0.66 * g * (1 + w), 0.84 * g * (1 - w), 0.66 * g * (1 + w));
      if (t > 2.3 && !s.pop) {
        s.pop = true; s.b.visible = false; play('shield_pop', { volume: 0.5 });
        for (let i = 0; i < 26; i++) { const a = Math.random() * TAU, e = Math.random() * 2 - 1, r = Math.sqrt(1 - e * e); this.ctx.fx.drop(Math.cos(a) * r * 0.66, 0.78 + e * 0.84, Math.sin(a) * r * 0.66, Math.cos(a) * r, 0.6 + e, Math.sin(a) * r, 0.012, 1, 2); }
      }
      return t > 2.7;
    },
  },
  // Sonar: rings of pings sweep out over everything
  sonar: {
    dur: 2.8,
    tick(s, t, dt, pose, c) {
      if (!s.go) { s.go = true; c.trigger('throw'); play('sonar_ping', { volume: 0.6 }); }
      s.k = s.k || 0;
      if (s.k < 3 && t > 0.2 + s.k * 0.35) {
        const k = s.k++;
        this.ctx.fx.ripple(0, this.ctx.deckY, 0, 0.2, 1.6 + k * 0.5, 0.9);
        const r = this._add(new THREE.Mesh(this.ring, this._ghost(0.8, 1.4))); r.rotation.x = Math.PI / 2; r.position.y = 0.9; r.userData.t0 = t; (s.rings || (s.rings = [])).push(r);
      }
      for (const r of s.rings || []) { const x = (t - r.userData.t0) / 0.9; r.scale.setScalar(0.2 + 1.6 * eOut(x)); r.material.opacity = 0.8 * (1 - c01(x)); r.position.y = 0.9 + x * 0.3; }
      return t > 2.2;
    },
  },
  // Strike: call it in — a missile drops onto the deck and the ink swirls up in a vortex
  strike: {
    dur: 3.2,
    tick(s, t, dt, pose, c) {
      if (!s.go) { s.go = true; c.trigger('throw'); play('strike_launch', { volume: 0.4 }); }
      const d = getSpecialProp('missile');
      if (t > 0.5 && !s.m && d) { s.m = this._add(propGroup(d, this.color, this.glow)); s.m.scale.setScalar(0.42); s.m.rotation.x = Math.PI / 2; play('strike_whistle', { volume: 0.4 }); }
      const to = this._ahead(0.4, 0.1, _v2);
      if (s.m && !s.hit) {
        const x = c01((t - 0.5) / 0.55);
        s.m.position.set(to.x, to.y + 3 * (1 - x) + 0.25, to.z); s.m.rotation.z += dt * 6;
        if (x >= 1) { s.hit = true; s.m.visible = false; this._blast(to, 1.5, 30); play('strike_impact', { volume: 0.5 }); s.t1 = t; }
      }
      if (s.hit && t - s.t1 < 1.3) {
        // the vortex: ink droplets spiralling up
        const tl = t - s.t1;
        for (let k = 0; k < 3; k++) { const a = tl * 9 + k * TAU / 3, r = 0.25 + tl * 0.1; this.ctx.fx.drop(to.x + Math.sin(a) * r, to.y + 0.05, to.z + Math.cos(a) * r, Math.cos(a) * 1.4, 2.2 + Math.random(), -Math.sin(a) * 1.4, 0.015, 1, 2); }
      }
      return t > 2.9;
    },
  },
  // Bazooka: shoulder the launcher and fire twisters
  zooka: {
    dur: 3.2,
    start(s, c) { c.setWeapon('sp_zooka'); s.swapped = true; },
    tick(s, t, dt, pose, c) {
      pose.firing = t > 0.35 && t < 2.4;
      s.next = s.next ?? 0.5;
      if (t >= s.next && t < 2.4) {
        s.next = t + 0.6; c.trigger('shoot'); play('zooka_fire', { volume: 0.4 });
        const m = _v.copy(FWD).multiplyScalar(0.55).setY(1.05);
        this._spray(m, FWD.clone().setY(0.15), 10, 3.2, 0.02, 1);
        (s.tw || (s.tw = [])).push({ t0: t });
      }
      for (const w of s.tw || []) {
        const tl = t - w.t0; if (tl > 0.9) continue;
        const p = _v2.copy(FWD).multiplyScalar(0.6 + tl * 1.6);
        for (let k = 0; k < 2; k++) { const a = tl * 16 + k * Math.PI, r = 0.08 + tl * 0.12; this.ctx.fx.drop(p.x + Math.sin(a) * r, 0.1 + tl * 0.8, p.z + Math.cos(a) * r, 0, 1.5, 0, 0.014, 1, 1.2); }
      }
      return t > 2.9;
    },
  },
  // Wailer: hold up the speaker, then blast sound waves forward
  wail: {
    dur: 3.4,
    start(s, c) {
      c.weaponHidden = true;
      if (c.weapon?.pivot) c.weapon.pivot.visible = false;
      const d = getSpecialProp('speaker');
      s.spk = this._add(d ? propGroup(d, this.color, this.glow) : new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.4, 0.8).translate(0, 0.7, 0), getPlasticMaterial()));
      s.spk.scale.setScalar(0.001); s.spk.rotation.y = KID_YAW;
      play('wail_charge', { volume: 0.4 });
    },
    tick(s, t, dt, pose, c) {
      const g = backOut(t / 0.35, 2);
      const p = _v.copy(FWD).multiplyScalar(0.42).setY(0.62 + 0.04 * Math.sin(t * 20) * c01((t - 0.6) * 3));
      s.spk.position.copy(p); s.spk.scale.setScalar(0.36 * g * (1 + 0.04 * Math.sin(t * 30) * c01(t - 0.9)));
      pose.subAim = true;
      if (t > 0.9 && !s.blast) { s.blast = true; s.t1 = t; play('wail_blast', { volume: 0.5 }); }
      if (s.blast) {
        s.k = s.k || 0;
        const tl = t - s.t1;
        if (s.k < 5 && tl > s.k * 0.22) { s.k++; const r = this._add(new THREE.Mesh(this.ring, this._ghost(0.85, 1.5))); r.rotation.y = KID_YAW; r.userData.t0 = t; (s.rings || (s.rings = [])).push(r); }
        for (const r of s.rings || []) { const x = (t - r.userData.t0) / 0.8; r.position.copy(FWD).multiplyScalar(0.6 + x * 1.4).setY(0.85); r.scale.setScalar(0.18 + 0.4 * x); r.material.opacity = 0.85 * (1 - c01(x)); }
      }
      if (t > 2.6) s.spk.scale.multiplyScalar(Math.exp(-dt * 10));
      return t > 3;
    },
  },
  // Kraken: become the kraken, bounce and slam
  kraken: {
    dur: 3.2,
    start(s, c) {
      const d = getSpecialProp('kraken');
      s.k = this._add(d ? propGroup(d, this.color, this.glow) : new THREE.Mesh(new THREE.CapsuleGeometry(0.7, 0.8, 6, 16).translate(0, 1.1, 0), getInkMaterial(this.color)));
      s.k.rotation.y = KID_YAW; s.k.visible = false;
      play('kraken_on', { volume: 0.5 });
    },
    tick(s, t, dt, pose, c) {
      if (t < 0.2) { pose.sy = 1 - 0.3 * (t / 0.2); return; }
      if (!s.on) { s.on = true; s.k.visible = true; this._blast(new THREE.Vector3(0, 0.6, 0), 1, 20); }
      pose.hide = t < 2.75;
      const tl = t - 0.2, hop = tl % 0.62 / 0.62, n = Math.floor(tl / 0.62);
      const sc = 0.58 * backOut(tl / 0.3, 2);
      const y = n < 3 ? 0.35 * Math.sin(Math.PI * hop) : 0;
      const sq = 1 + 0.18 * Math.cos(TAU * hop) * (n < 3 ? 1 : 0);
      s.k.position.set(0, this.ctx.deckY + y, 0); s.k.scale.set(sc / Math.sqrt(sq), sc * sq, sc / Math.sqrt(sq));
      s.k.rotation.y = KID_YAW + Math.sin(tl * 3) * 0.3;
      if (n !== s.n && n > 0 && n <= 3) { s.n = n; this.ctx.fx.crown(0, this.ctx.deckY, 0, 1.1, 24, 0.35); play(n === 3 ? 'kraken_slam' : 'kraken_jump', { volume: 0.4 }); }
      if (t > 2.75 && s.k.visible) { s.k.visible = false; this._blast(new THREE.Vector3(0, 0.6, 0), 1.1, 22); c.trigger('land', 8); }
      return t > 3.05;
    },
  },
  // Blower: blow a giant bubble, then it pops in a huge blast
  blower: {
    dur: 3.4,
    start(s, c) { c.setWeapon('sp_blower'); s.swapped = true; play('blower_inflate', { volume: 0.4 }); },
    tick(s, t, dt, pose, c) {
      pose.firing = t > 0.3 && t < 1.8;
      if (!s.b) { s.b = this._add(new THREE.Mesh(this.sphere, this._ghost(0.3, 1.25))); s.b.scale.setScalar(0.001); }
      const g = eOut((t - 0.3) / 1.4), w = Math.sin(t * 8) * 0.04;
      const p = _v.copy(FWD).multiplyScalar(0.45 + 0.35 * g).setY(0.8 + 0.1 * g);
      s.b.position.copy(p); s.b.scale.set(0.42 * g * (1 + w), 0.42 * g * (1 - w), 0.42 * g * (1 + w));
      if (t > 2.2 && !s.pop) { s.pop = true; s.b.visible = false; play('bubble_blast', { volume: 0.5 }); this._blast(p.clone(), 1.4, 28); }
      return t > 3;
    },
  },
  // Jetpack: the pack replaces the tank, hover and fire blasts, exhaust spraying down
  jetpack: {
    dur: 3.6,
    start(s, c) {
      c.setWeapon('sp_jetgun'); s.swapped = true;
      const tank = c.tank?.group, d = getSpecialProp('jetpack');
      if (tank) {
        s.tankKids = tank.children.filter((k) => k.visible);
        for (const k of s.tankKids) k.visible = false;
        s.pack = d ? propGroup(d, this.color, this.glow) : new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.36, 0.16).translate(0, 0.1, -0.05), getInkMaterial(this.color));
        tank.add(s.pack); s.tank = tank;
        s.noz = d && d.nozzles ? d.nozzles.map((n) => n.clone()) : [new THREE.Vector3(-0.08, -0.12, -0.05), new THREE.Vector3(0.08, -0.12, -0.05)];
      }
      play('jet_fire', { volume: 0.4 });
    },
    tick(s, t, dt, pose, c) {
      const up = eOut(t / 0.5) * (1 - eOut((t - 2.9) / 0.4));
      pose.y = 0.6 * up + 0.06 * Math.sin(t * 4.4) * up; pose.grounded = up < 0.05;
      pose.firing = t > 0.7 && t < 2.6;
      if (s.tank && up > 0.05) for (const n of s.noz) {
        s.tank.localToWorld(_v.copy(n)); this.root.worldToLocal(_v);
        this.ctx.fx.drop(_v.x, _v.y, _v.z, (Math.random() - 0.5) * 0.4, -3.2, (Math.random() - 0.5) * 0.4, 0.013, 1, 1.5);
      }
      s.next = s.next ?? 0.8;
      if (t >= s.next && t < 2.6) { s.next = t + 0.45; c.trigger('shoot'); play('jet_fire', { volume: 0.3 }); this._spray(_v.copy(FWD).multiplyScalar(0.5).setY(1.0 + pose.y), FWD.clone().setY(-0.35), 8, 3.4, 0.022, 2); }
      return t > 3.35;
    },
    end(s) { s.pack?.removeFromParent(); for (const k of s.tankKids || []) k.visible = true; },
  },
  // Stamp: three heavy smashes in front
  stamp: {
    dur: 3.2,
    start(s, c) { c.setWeapon('sp_stamp'); s.swapped = true; },
    tick(s, t, dt, pose, c) {
      s.n = s.n || 0;
      if (s.n < 3 && t > 0.4 + s.n * 0.7) { s.n++; c.trigger('flick', s.n % 2 ? 1 : -1); play('stamp_swing', { volume: 0.4 }); s.hitAt = t + 0.18; }
      if (s.hitAt && t >= s.hitAt) { s.hitAt = 0; play('stamp_slam', { volume: 0.5 }); this._blast(this._ahead(0.5, 0), 1.1, 20); }
      return t > 2.8;
    },
  },
  // Booyah: hold up an orb that charges, then throw it for a huge blast
  booyah: {
    dur: 3.6,
    start(s, c) {
      c.subPropHidden = true;
      s.ball = this._add(new THREE.Mesh(this.sphere, this._mat(new THREE.MeshBasicMaterial({ color: this.color.clone().multiplyScalar(2) }))));
      s.halo = this._add(new THREE.Mesh(this.sphere, this._ghost(0.3, 1.4)));
      play('booyah_charge', { volume: 0.4 });
    },
    tick(s, t, dt, pose, c) {
      const ch = c01(t / 1.4);
      if (!s.thrown) {
        pose.subAim = true;
        const h = this._head(c, _v);
        const r = 0.12 + 0.14 * ch, pul = 1 + Math.sin(t * (6 + ch * 12)) * 0.06;
        s.ball.position.set(h.x, h.y + 0.32 + r, h.z); s.halo.position.copy(s.ball.position);
        s.ball.scale.setScalar(r * pul); s.halo.scale.setScalar(r * 1.35 * pul);
        if (Math.random() < 0.4) this.ctx.sparks.spawn(s.ball.position.x + (Math.random() - 0.5) * 0.3, s.ball.position.y, s.ball.position.z + (Math.random() - 0.5) * 0.3, 0.06, this.color, 0.5);
        if (t > 1.7) { s.thrown = true; s.t1 = t; s.from = s.ball.position.clone(); c.trigger('throw'); play('booyah_throw', { volume: 0.5 }); }
      } else if (!s.boom) {
        const x = c01((t - s.t1) / 0.5), to = this._ahead(0.45, 0, _v2);
        s.ball.position.lerpVectors(s.from, to, x); s.ball.position.y += 0.4 * 4 * x * (1 - x); s.halo.position.copy(s.ball.position);
        if (x >= 1) { s.boom = true; s.ball.visible = false; s.halo.visible = false; play('booyah_blast', { volume: 0.5 }); this._blast(to.clone(), 1.7, 34); }
      }
      return t > 3.2;
    },
  },
  // Zipline: the aura flares up, a grapple line shoots out and the kid zips along it and back
  zipcaster: {
    dur: 3,
    tick(s, t, dt, pose, c) {
      if (!s.line) {
        s.line = this._add(new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 1, 6).translate(0, 0.5, 0), this._mat(new THREE.MeshBasicMaterial({ color: this.color.clone().multiplyScalar(1.6) }))));
        s.end = _v2.copy(FWD).multiplyScalar(0.9).addScaledVector(SIDE, 0.35).setY(1.9).clone();
        play('zip_fire', { volume: 0.4 });
      }
      for (let i = 0; i < 2; i++) { const a = Math.random() * TAU; this.ctx.sparks.spawn(Math.sin(a) * 0.35, 0.2 + Math.random() * 1.2, Math.cos(a) * 0.35, 0.05, this.color, 0.4); }
      const shoot = c01((t - 0.3) / 0.25), from = _v.set(0, 1.1 + pose.y, 0);
      const tip = from.clone().lerp(s.end, shoot);
      s.line.position.copy(from); s.line.scale.set(1, from.distanceTo(tip) || 0.001, 1);
      s.line.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), tip.clone().sub(from).normalize());
      s.line.visible = t > 0.3 && t < 1.6;
      if (t > 0.6 && t < 1.6) { const z = Math.sin(Math.PI * c01((t - 0.6) / 1.0)); pose.y = 0.55 * z; pose.grounded = false; pose.twirl = 0.3 * z; if (!s.pull) { s.pull = true; play('zip_pull', { volume: 0.4 }); } }
      if (t > 1.6 && !s.land) { s.land = true; c.trigger('land', 8); this.ctx.fx.crown(0, this.ctx.deckY, 0, 1, 22, 0.3); }
      return t > 2.6;
    },
  },
  // Crab Rig: climb in, walk the legs, spin up the gatling, lob a mortar, roll into the armoured ball and back
  crab: {
    dur: 5,
    start(s, c) {
      const d = getSpecialProp('crab');
      s.rig = this._add(new THREE.Group());
      s.hull = new THREE.Group(); s.rig.add(s.hull);
      s.S = 0.62;   // the whole ride (kid included) is scaled to fit the pedestal
      s.rig.scale.setScalar(0.001); s.hull.rotation.y = KID_YAW;
      if (d) {
        s.hull.add(propGroup(d, this.color, this.glow));
        s.legs = (d.legs || []).map((L, i) => { const g = pivotGroup(L.body, L.ink, L.at, this.color); s.hull.add(g); return { g, i }; });
        if (d.gun) s.hull.add(partGroup(d.gun.body, d.gun.ink, this.color));
        if (d.cannon) s.hull.add(partGroup(d.cannon.body, d.cannon.ink, this.color));
        if (d.gunSpin) { s.spin = pivotGroup(d.gunSpin, d.gunSpinInk, d.gunSpinAt, this.color); s.hull.add(s.spin); }
        s.muzzle = (d.gunMuzzle || new THREE.Vector3(0, 0.7, 1)).clone(); s.cannon = (d.cannonMuzzle || new THREE.Vector3(0.5, 1.1, -0.2)).clone();
        s.seat = d.seatAt ? d.seatAt.y : 0.95;
        const R = d.ballR || 0.95; s.R = R;
        s.ball = new THREE.Group(); s.ball.position.y = R; const bm = partGroup(d.ballBody, d.ballInk, this.color); bm.position.y = -R; s.ball.add(bm); s.ball.visible = false; s.rig.add(s.ball);
      } else {
        s.hull.add(new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.9, 1.6).translate(0, 0.55, 0), getInkMaterial(this.color)));
        s.muzzle = new THREE.Vector3(0, 0.7, 1); s.cannon = new THREE.Vector3(0.5, 1.1, -0.2); s.seat = 0.95; s.R = 0.95;
        s.ball = new THREE.Mesh(this.sphere, getInkMaterial(this.color)); s.ball.scale.setScalar(0.95); s.ball.position.y = 0.95; s.ball.visible = false; s.rig.add(s.ball);
      }
      c.weaponHidden = true;
      if (c.weapon?.pivot) c.weapon.pivot.visible = false;
      if (c.weapon?.left?.pivot) c.weapon.left.pivot.visible = false;
      play('crab_move', { volume: 0.3 });
    },
    tick(s, t, dt, pose, c) {
      const S = s.S, deck = this.ctx.deckY;
      const g = backOut(t / 0.4, 1.6), out = 1 - eOut((t - 4.5) / 0.3);
      s.rig.scale.setScalar(Math.max(0.001, S * g * out));
      s.rig.position.set(0, deck, 0);
      if (t < 0.4) { this.ctx.fx.crown(0, deck, 0, 0.3, 2, 0.4); }
      // the kid rides in the hatch (scaled with the rig)
      pose.scale = S; pose.y = s.seat * S * g; pose.grounded = true;
      const roll = t > 2.6 && t < 4.3;
      // walking: legs paddle, the hull rocks and turns a little
      s.legT = (s.legT || 0) + dt * 6;
      for (const L of s.legs || []) L.g.rotation.x = Math.sin(s.legT + L.i * 1.7) * 0.28 * (roll ? 0 : 1);
      s.hull.rotation.y = KID_YAW + Math.sin(t * 1.3) * 0.15;
      s.hull.position.y = Math.abs(Math.sin(s.legT)) * 0.03;
      // gatling 0.6 → 1.8: barrels spin up, a stream of slime blobs
      const gat = t > 0.6 && t < 1.8;
      if (s.spin) s.spin.rotation.z += dt * (gat ? 40 : 2);
      if (gat) {
        s.gt = (s.gt || 0) - dt;
        if (s.gt <= 0) {
          s.gt = 0.06;
          const m = s.hull.localToWorld(_v.copy(s.muzzle)); this.root.worldToLocal(m);
          const f = _v2.set(Math.sin(s.hull.rotation.y), 0.08, Math.cos(s.hull.rotation.y));
          this.ctx.fx.drop(m.x, m.y, m.z, f.x * 4.5 + (Math.random() - 0.5) * 0.5, f.y * 4 + Math.random() * 0.6, f.z * 4.5 + (Math.random() - 0.5) * 0.5, 0.022, 1, 1.4);
          if (!s.gs || t - s.gs > 0.12) { s.gs = t; play('crab_gatling', { volume: 0.25 }); }
        }
        pose.firing = true;
      }
      // mortar at 2.0
      if (t > 2.0 && !s.mortar) {
        s.mortar = true; play('crab_cannon', { volume: 0.5 });
        const m = s.hull.localToWorld(_v.copy(s.cannon)); this.root.worldToLocal(m);
        this.ctx.fx.drop(m.x, m.y, m.z, Math.sin(KID_YAW) * 1.3, 3.6, Math.cos(KID_YAW) * 1.3, 0.06, 2, 3);
        this.ctx.fx.crown(0, deck, 0, 0.5, 10, 0.4);
      }
      // roll into the armoured ball: a lap round the deck
      s.hull.visible = !roll; s.ball.visible = roll; pose.hide = roll;
      if (roll && !s.rolled) { s.rolled = true; play('crab_roll', { volume: 0.4 }); this._blast(new THREE.Vector3(0, 0.4, 0), 0.6, 12); }
      if (roll) {
        const tl = t - 2.6, a = KID_YAW + tl * 3.8, rr = 0.32 / S * Math.min(1, tl * 3) * (1 - c01((tl - 1.4) * 4));
        s.ball.position.set(Math.sin(a) * rr, s.R, Math.cos(a) * rr);
        s.ball.rotation.set(tl * 3.8 * rr / s.R, a + Math.PI / 2, 0, 'YXZ');
        s.trail = (s.trail || 0) + dt;
        if (s.trail > 0.05) { s.trail = 0; const w = s.rig.localToWorld(_v.set(s.ball.position.x, 0, s.ball.position.z)); this.root.worldToLocal(w); this.ctx.fx.splat(w.x, deck, w.z, 0.12, 2.5); }
      }
      if (t > 4.3 && !s.unroll) { s.unroll = true; this._blast(new THREE.Vector3(0, 0.5, 0), 0.8, 16); c.trigger('land', 8); }
      if (t > 4.5 && !s.broke) { s.broke = true; play('crab_break', { volume: 0.4 }); this._blast(new THREE.Vector3(0, 0.4, 0), 1, 18); }
      if (t > 4.5) { pose.scale = 1; pose.y = 0; }
      return t > 4.85;
    },
  },
  // anything without its own scene yet: a power-up flourish
  generic: {
    dur: 2.4,
    tick(s, t, dt, pose, c) {
      if (!s.go) { s.go = true; c.setDance?.('victory'); c.trigger('hairflip'); play('special_activate', { volume: 0.5 }); this._blast(new THREE.Vector3(0, 0.2, 0), 0.9, 20); }
      return t > 2.2;
    },
  },
};
